import { Quote } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useOpenDaily } from "@/book/components/daily";
import { ReviewForm } from "@/book/components/review-form";
import { FunctionTile, TILE_CLASS, TileGrid } from "@/book/components/function-tile";
import { ChapterList } from "@/book/components/rows";
import { DAILY, HOME_TILES } from "@/book/nav";
import { useChapters, useMyReviews } from "@/shared/api/book";
import { cn } from "@/shared/lib/utils";
import { SectionLabel } from "@/shared/ui/section-label";
import { Sheet } from "@/shared/ui/sheet";
import { ListSkeleton } from "@/shared/ui/state";

/** Melléklet-kezdőoldal (design-rendszer 7.): vélemény-kártya → funkció-csempék (az elsőn a
 *  mai gyakorlat; a lista a `nav.ts`-ből jön) → „Fejezetek" sorok. */
export function HomePage() {
  const { data: chapters, isPending } = useChapters();
  const { data: reviews } = useMyReviews();
  const [reviewOpen, setReviewOpen] = useState(false);
  const { hash } = useLocation();
  const chaptersRef = useRef<HTMLElement>(null);
  const top = chapters ?? [];

  // A fejezet-listának nincs külön oldala: a `#fejezetek` ide, ehhez a szakaszhoz ugrik.
  useEffect(() => {
    if (hash === "#fejezetek" && !isPending) {
      chaptersRef.current?.scrollIntoView({ block: "start" });
    }
  }, [hash, isPending]);

  return (
    <div className="flex flex-col gap-[22px]">
      {/* Látható megszólítás nincs; a lapnak viszont kell egy címe a képernyőolvasóknak. */}
      <h1 className="sr-only">Digitális melléklet</h1>

      {reviews !== undefined && reviews.length === 0 && (
        <button
          type="button"
          onClick={() => setReviewOpen(true)}
          className="flex items-center justify-between gap-3 rounded-lg border border-line bg-cream px-4 py-3.5 text-left transition-colors hover:bg-gold-soft"
        >
          <span className="flex flex-col gap-0.5">
            <span className="text-[15px] font-semibold">Mi a véleményed a könyvről?</span>
            <span className="text-[13px] leading-[1.4] text-muted">
              Örömmel vesszük, ha megosztod velünk az élményed.
            </span>
          </span>
          <Quote size={20} className="shrink-0 text-gold" strokeWidth={1.8} />
        </button>
      )}

      <section className="flex flex-col gap-2.5">
        <TileGrid>
          <DailyTile />
          {HOME_TILES.map((item) => (
            <FunctionTile key={item.to} {...item} />
          ))}
        </TileGrid>
      </section>

      <section id="fejezetek" ref={chaptersRef} className="flex flex-col gap-0.5 scroll-mt-4">
        <SectionLabel className="mb-2">Fejezetek</SectionLabel>
        {isPending && <ListSkeleton rows={3} />}
        {!isPending && top.length === 0 && (
          <p className="border-t border-line py-3 text-[13px] text-muted">
            A könyv fejezetei még nincsenek feltöltve.
          </p>
        )}
        {/* Az alfejezetekkel együtt — különben a 4. fejezethez nem tartozna semmi. */}
        <ChapterList chapters={top} />
      </section>

      <Sheet
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        title="Mi a véleményed a könyvről?"
        description="Jóváhagyás után jelenik meg — ha engeded, a könyv publikus oldalán is."
      >
        <ReviewForm onDone={() => setReviewOpen(false)} />
      </Sheet>
    </div>
  );
}

/** A mai gyakorlat csempéje: ha még nem kért mára, a koppintás húz egyet és megnyitja;
 *  ha már van, egyből a mai gyakorlatra visz (rendszerterv 8.). Ha van már húzott gyakorlat,
 *  a csempe arany keretet kap és a címét mutatja. */
function DailyTile() {
  const daily = useOpenDaily();
  const Icon = DAILY.icon;
  return (
    <button
      type="button"
      onClick={daily.open}
      disabled={daily.disabled}
      className={cn(
        TILE_CLASS,
        daily.exercise ? "border-gold bg-gold-soft" : "hover:bg-paper",
        "disabled:opacity-60",
      )}
    >
      <Icon size={22} strokeWidth={1.8} className="text-gold" />
      <span className="flex flex-col gap-0.5">
        <span className="text-[13px] font-semibold leading-tight">{DAILY.label}</span>
        <span className="line-clamp-2 text-[11px] leading-tight text-muted">{daily.label}</span>
      </span>
    </button>
  );
}
